import React, { useState, useEffect, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout
} from '@stripe/react-stripe-js';
import {
  Box,
  Button,
  Typography,
  CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DocumentPageOverlay from './DocumentPageOverlay';
import { DOCUMENT } from '../constants/documentTheme';
import {
  PARTNER_DEVELOPER_API_MONTHLY,
  PARTNER_DEVELOPER_API_TIER_TYPE,
  PARTNER_DEVELOPER_API_PRODUCTION_INCLUDED,
  PARTNER_DEVELOPER_API_OVERAGE_UNIT,
  PARTNER_DEVELOPER_API_OVERAGE_CENTS
} from '../config/plans';

// Debug Stripe key in production
console.log('🔍 Stripe Key Debug:', {
  keyExists: !!(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || process.env.STRIPE_PUBLISHABLE_KEY),
  keyLength: (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || process.env.STRIPE_PUBLISHABLE_KEY)?.length,
  keyPrefix: (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || process.env.STRIPE_PUBLISHABLE_KEY)?.substring(0, 25),
  keyValue: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || process.env.STRIPE_PUBLISHABLE_KEY,
  nodeEnv: process.env.NODE_ENV
});

let stripePromise;
const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || process.env.STRIPE_PUBLISHABLE_KEY;

if (!stripeKey) {
  console.error('❌ STRIPE_PUBLISHABLE_KEY is not defined');
  stripePromise = null;
} else if (!stripeKey.startsWith('pk_')) {
  console.error('❌ Invalid Stripe publishable key format:', stripeKey);
  stripePromise = null;
} else {
  try {
    stripePromise = loadStripe(stripeKey);
    console.log('✅ Stripe loadStripe called successfully');
  } catch (error) {
    console.error('❌ Error loading Stripe:', error);
    stripePromise = null;
  }
}

const StripeCheckout = ({
  open,
  onClose,
  clientSecret,
  tripTitle,
  upgradeType,
  quantity = 1,
  onSuccess
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [checkoutInstance, setCheckoutInstance] = useState(0);

  const handleClose = useCallback(() => {
    setCheckoutInstance((value) => value + 1);
    onClose?.();
  }, [onClose]);

  const options = {
    clientSecret,
    onComplete: async () => {
      // Embedded Checkout calls onComplete with no args when payment succeeds
      console.log('🎉 Embedded Checkout completed');

      try {
        const sessionId = clientSecret.split('_secret_')[0];
        console.log('🔍 Extracted session ID:', sessionId);
        console.log('🚀 Calling payment verification endpoint...');

        const api = (await import('../config/api')).default;
        const response = await api.post('/payments/verify-payment', {
          sessionId: sessionId
        });

        console.log('✅ Payment verification response:', response.data);

        if (response.data?.tripId) {
          sessionStorage.setItem(`payment_completed_trip_${response.data.tripId}`, 'true');
        }

        if (onSuccess) {
          console.log('🎯 Calling onSuccess callback...');
          onSuccess(response.data);
        }
      } catch (error) {
        console.error('❌ Error verifying payment:', error);
        console.error('❌ Error details:', error.response?.data || error.message);
        // Payment succeeded on Stripe — still notify UI so the user isn't stuck
        if (onSuccess) {
          console.log('🎯 Calling onSuccess callback despite verification error...');
          onSuccess({ success: false, error: error.response?.data?.error || error.message });
        }
      }
    }
  };

  useEffect(() => {
    if (clientSecret) {
      setLoading(false);
    }
  }, [clientSecret]);

  useEffect(() => {
    if (!open) {
      setCheckoutInstance((value) => value + 1);
    }
  }, [open]);

  if (!clientSecret) {
    return null;
  }

  const checkoutTitle = upgradeType === PARTNER_DEVELOPER_API_TIER_TYPE
    ? t('partnerPlans.developerApi.checkoutTitle', { fee: PARTNER_DEVELOPER_API_MONTHLY })
    : (tripTitle || t('payment.checkoutTitle'));
  const showDeveloperApiSummary = upgradeType === PARTNER_DEVELOPER_API_TIER_TYPE;
  const developerApiItems = showDeveloperApiSummary
    ? [
        t('partnerPlans.developerApi.checkoutItemKeys'),
        t('partnerPlans.developerApi.checkoutItemWebhooks'),
        t('partnerPlans.developerApi.checkoutItemQuota', {
          included: PARTNER_DEVELOPER_API_PRODUCTION_INCLUDED.toLocaleString()
        }),
        t('partnerPlans.developerApi.checkoutItemOverage', {
          overage: PARTNER_DEVELOPER_API_OVERAGE_CENTS / 100,
          unit: PARTNER_DEVELOPER_API_OVERAGE_UNIT.toLocaleString()
        }),
        t('partnerPlans.developerApi.checkoutItemPackages')
      ]
    : [];

  return (
    <DocumentPageOverlay
      open={open}
      onClose={handleClose}
      variant="compact"
      title={checkoutTitle}
      testId="stripe-checkout-overlay"
      dialogZIndex={1400}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 320, bgcolor: '#f8f9fa' }}>
        {showDeveloperApiSummary && (
          <Box
            sx={{
              px: 2.5,
              py: 2,
              bgcolor: DOCUMENT.surface,
              borderBottom: `1px solid ${DOCUMENT.rule}`
            }}
          >
            <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink2, lineHeight: 1.55, mb: 1.25 }}>
              {t('partnerPlans.developerApi.checkoutLead', { fee: PARTNER_DEVELOPER_API_MONTHLY })}
            </Typography>
            {developerApiItems.map((item) => (
              <Typography key={item} sx={{ fontSize: 13, color: DOCUMENT.ink, mb: 0.65, lineHeight: 1.45 }}>
                ✓ {item}
              </Typography>
            ))}
            <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, mt: 1.25 }}>
              {t('partnerPlans.developerApi.checkoutDue', { fee: PARTNER_DEVELOPER_API_MONTHLY })}
            </Typography>
          </Box>
        )}
        <Box sx={{ flex: '1 1 auto', minHeight: 0 }}>
          {loading && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 400,
            }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && stripePromise && (
            <Box sx={{ minHeight: 320 }}>
              <EmbeddedCheckoutProvider
                stripe={stripePromise}
                options={options}
                key={`${clientSecret}-${checkoutInstance}`}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </Box>
          )}

          {!loading && !stripePromise && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="error" variant="h6">
                {t('payment.configErrorTitle')}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {t('payment.configErrorBody')}
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            px: 2.5,
            py: 2,
            borderTop: `1px solid ${DOCUMENT.rule}`,
            bgcolor: DOCUMENT.surface,
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              minWidth: 200,
              borderColor: DOCUMENT.oxblood,
              color: DOCUMENT.oxblood,
              '&:hover': { borderColor: DOCUMENT.oxblood, bgcolor: 'rgba(91,15,20,0.04)' }
            }}
          >
            {t('payment.cancelCheckout')}
          </Button>
        </Box>
      </Box>
    </DocumentPageOverlay>
  );
};

export default StripeCheckout;
